/**
 * Author: Claude Code
 * Version: v3.0.107
 * Modified: 2025-09-19 Beijing Time
 * 
 * CombatTheaterWindow - 剧院式战斗窗口组件
 * 独立的模块化组件，提供：
 * - 20%大小的居中战斗窗口
 * - 80%暗色遮罩背景
 * - 两个agent面对面布局
 * - 可复用的战斗窗口管理
 */

import * as Phaser from 'phaser';
import { getCharacterFrameKey, colorToCharacterType } from '../animation/utils/spriteKeyUtils';
import { AnimationType } from '../animation/spriteTypes';

export interface CombatTheaterConfig {
  windowWidthPercent: number;    // 窗口宽度百分比 (默认20%)
  windowHeightPercent: number;   // 窗口高度百分比 (默认20%)
  overlayAlpha: number;          // 遮罩透明度 (默认0.8)
  overlayColor: number;          // 遮罩颜色 (默认黑色)
  agentScale: number;            // Agent缩放比例
  agentSpacing: number;          // Agent之间的间距
}

export interface CombatTheaterOptions {
  mode?: 'center' | 'pin';
  pinnedAgentId?: string;
  pinnedOffsetY?: number;
}

export const DEFAULT_THEATER_CONFIG: CombatTheaterConfig = {
  windowWidthPercent: 0.2,
  windowHeightPercent: 0.2,
  overlayAlpha: 0.8,
  overlayColor: 0x000000,
  agentScale: 2.0,
  agentSpacing: 40
};

const DEPTH_OVERLAY = 10000;
const DEPTH_THEATER = 10010;

export class CombatTheaterWindow {
  private scene: Phaser.Scene;
  private config: CombatTheaterConfig;
  
  // 主要组件
  private theaterContainer?: Phaser.GameObjects.Container;
  private overlay?: Phaser.GameObjects.Rectangle;
  private combatWindow?: Phaser.GameObjects.Container;
  private windowBackground?: Phaser.GameObjects.Rectangle;
  
  // 战斗Agent
  private leftAgent?: Phaser.GameObjects.Sprite;
  private rightAgent?: Phaser.GameObjects.Sprite;
  private leftColor: string = 'green';
  private rightColor: string = 'red';
  
  // HP条系统
  private leftHPBarBg?: Phaser.GameObjects.Rectangle;
  private leftHPBar?: Phaser.GameObjects.Rectangle;
  private leftHPText?: Phaser.GameObjects.Text;
  private rightHPBarBg?: Phaser.GameObjects.Rectangle;
  private rightHPBar?: Phaser.GameObjects.Rectangle;
  private rightHPText?: Phaser.GameObjects.Text;
  private hpState = {
    left: { current: 0, max: 0 },
    right: { current: 0, max: 0 }
  };
  
  // 状态
  private isActive: boolean = false;
  private leftWeaponSprite?: Phaser.GameObjects.Sprite;
  private rightWeaponSprite?: Phaser.GameObjects.Sprite;
  private followMode: 'center' | 'pin' = 'center';
  private pinnedAgentId?: string;
  private pinnedOffsetY: number = 80;
  private resizeListener?: (gameSize: Phaser.Structs.Size) => void;
  
  constructor(scene: Phaser.Scene, config?: Partial<CombatTheaterConfig>) {
    this.scene = scene;
    this.config = { ...DEFAULT_THEATER_CONFIG, ...config };
  }

  /**
   * 创建剧院模式 - 主入口方法
   */
  createTheaterMode(
    leftAgentKey: string,
    rightAgentKey: string,
    leftAgentColor: string,
    rightAgentColor: string,
    options?: CombatTheaterOptions
  ): void {
    if (this.isActive) {
      console.warn('⚠️ CombatTheaterWindow: 剧院模式已激活');
      return;
    }

    console.log('🎭 CombatTheaterWindow: 创建剧院模式');

    const opts: CombatTheaterOptions = options ?? {};
    this.followMode = opts.mode ?? 'pin';
    this.pinnedAgentId = this.followMode === 'pin'
      ? (opts.pinnedAgentId || rightAgentKey)
      : undefined;
    this.pinnedOffsetY = typeof opts.pinnedOffsetY === 'number' ? opts.pinnedOffsetY : 80;

    // 创建主容器
    this.createMainContainer();
    
    // 创建暗色遮罩 (80%背景)
    this.createOverlay();
    
    // 创建战斗窗口 (20%中心窗口)
    this.createCombatWindow();
    
    // 记录颜色
    this.leftColor = leftAgentColor || 'green';
    this.rightColor = rightAgentColor || 'red';

    // 先确保动画存在（idle/atk/rush/hurt/die），再创建/播放sprite，消除初次 Missing idle 报错
    try {
      this.ensureAnimationsForColor(this.leftColor);
      this.ensureAnimationsForColor(this.rightColor);
    } catch (e) {
      console.warn('Ensure theater animations failed:', (e as Error).message);
    }

    // 最后再面对面布局与播放 idle
    this.positionAgentsFaceToFace(leftAgentKey, rightAgentKey, leftAgentColor, rightAgentColor);
    
    // 设置深度层级
    this.setupDepthLayers();

    // 计算初始位置 & 跟随
    this.startFollowing();
    
    // 不进行任何占位HP绘制，等待战斗事件提供真实 current/max
    
    this.isActive = true;
    console.log('✅ CombatTheaterWindow: 剧院模式激活完成，HP条初始化完成');
  }

  // 兼容性方法：为给定颜色创建所需动画（若不存在），同时兼容帧纹理键的两种命名风格
  private ensureAnimationsForColor(color: string): void {
    const charType = colorToCharacterType(color as any) as any;
    const MAP: Array<{ name: string; type: AnimationType; frames: number; fps: number; repeat: number }> = [
      { name: 'idle', type: AnimationType.IDLE, frames: 11, fps: 10, repeat: -1 },
      { name: 'mov',  type: AnimationType.MOVE, frames: 8,  fps: 16, repeat: -1 },
      { name: 'hurt', type: AnimationType.HURT, frames: 3,  fps: 15, repeat: 0 },
      { name: 'die',  type: AnimationType.DIE,  frames: 12, fps: 8,  repeat: 0 },
      { name: 'rush', type: AnimationType.RUSH, frames: 11, fps: 20, repeat: 0 },
      { name: 'use',  type: AnimationType.USE,  frames: 11, fps: 10, repeat: 0 },
      { name: 'atk',  type: AnimationType.ATTACK, frames: 8, fps: 15, repeat: 0 },
    ];

    const exists = (key: string) => this.scene.anims.exists(key);
    for (const m of MAP) {
      const keyHyphen = `${color}-${m.name}`;
      const keyUnderscore = `${color}_${m.name}`;
      if (!exists(keyHyphen)) {
        const frames: { key: string; frame: null }[] = [];
        for (let i = 0; i < m.frames; i++) {
          frames.push({ key: getCharacterFrameKey(charType, m.type, i), frame: null });
        }
        const allExist = frames.every(f => this.scene.textures.exists(f.key));
        if (allExist) {
          this.scene.anims.create({ key: keyHyphen, frames, frameRate: m.fps, repeat: m.repeat });
        } else {
          console.warn(`Missing frames for ${keyHyphen}; animation not created`);
        }
      }
      if (!exists(keyUnderscore) && exists(keyHyphen)) {
        const frames: { key: string; frame: null }[] = [];
        for (let i = 0; i < m.frames; i++) {
          frames.push({ key: getCharacterFrameKey(charType, m.type, i), frame: null });
        }
        this.scene.anims.create({ key: keyUnderscore, frames, frameRate: m.fps, repeat: m.repeat });
      }
    }
  }

  /**
   * 创建主容器
   */
  private createMainContainer(): void {
    this.theaterContainer = this.scene.add.container(0, 0);
    (this.theaterContainer as any).setScrollFactor?.(0);
    this.theaterContainer.setDepth(DEPTH_OVERLAY);
  }

  /**
   * 创建80%暗色遮罩
   */
  private createOverlay(): void {
    const cam = this.scene.cameras.main;
    const width = cam.width;
    const height = cam.height;

    this.overlay = this.scene.add.rectangle(
      0,
      0,
      width,
      height,
      this.config.overlayColor,
      this.config.overlayAlpha
    )
      .setOrigin(0)
      .setScrollFactor(0)
      .setDepth(DEPTH_OVERLAY)
      .setInteractive();

    this.overlay.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      pointer.event?.stopPropagation?.();
    });

    this.theaterContainer!.add(this.overlay);

    this.resizeListener = (gameSize: Phaser.Structs.Size) => {
      if (!this.overlay) return;
      this.overlay.setSize(gameSize.width, gameSize.height);
      this.overlay.setPosition(0, 0);
      if (this.followMode === 'pin') {
        this.handleFollowUpdate();
      } else {
        this.centerCombatWindow();
      }
    };
    this.scene.scale.on('resize', this.resizeListener, this);

    console.log(`🌑 创建遮罩: ${width}x${height}, alpha=${this.config.overlayAlpha}`);
  }

  /**
   * 创建20%中心战斗窗口
   */
  private createCombatWindow(): void {
    const cam = this.scene.cameras.main;
    const windowWidth = cam.width * this.config.windowWidthPercent;
    const windowHeight = cam.height * this.config.windowHeightPercent;
    
    this.combatWindow = this.scene.add.container(0, 0);
    (this.combatWindow as any).setScrollFactor?.(0);
    
    // 创建窗口背景 (稍浅的颜色用于区分)
    this.windowBackground = this.scene.add.rectangle(
      0, 0, // 容器内相对位置
      windowWidth,
      windowHeight,
      0x333333, // 深灰色背景
      0.9
    );
    
    // 添加边框效果
    this.windowBackground.setStrokeStyle(3, 0x666666);
    
    this.combatWindow.add(this.windowBackground);
    this.combatWindow.setDepth(DEPTH_THEATER);
    this.theaterContainer!.add(this.combatWindow);

    console.log(`⚔️ 创建战斗窗口: ${windowWidth}x${windowHeight}`);
  }

  private centerCombatWindow(): void {
    if (!this.combatWindow) return;
    const cam = this.scene.cameras.main;
    this.combatWindow.setPosition(cam.width / 2, cam.height * 0.5);
  }

  private startFollowing(): void {
    this.stopFollowing();
    this.scene.events.on('update', this.handleFollowUpdate, this);
    this.handleFollowUpdate();
  }

  private stopFollowing(): void {
    this.scene.events.off('update', this.handleFollowUpdate, this);
  }

  private handleFollowUpdate = (): void => {
    if (!this.combatWindow) return;
    if (this.followMode === 'pin') {
      const pos = this.findAgentWorldPosition(this.pinnedAgentId);
      if (pos) {
        const cam = this.scene.cameras.main;
        const worldView = cam.worldView;
        const screenX = (pos.x - worldView.x) * cam.zoom;
        const screenY = (pos.y - worldView.y) * cam.zoom - this.pinnedOffsetY;
        this.combatWindow.setPosition(screenX, screenY);
        return;
      }
    }
    this.centerCombatWindow();
  };

  private findAgentWorldPosition(agentId?: string): { x: number; y: number } | null {
    if (!agentId) return null;
    const battleScene = this.scene as any;
    const pma = battleScene?.phaserAnimationManager;
    const container: Phaser.GameObjects.Container | undefined =
      pma?.agentContainers?.get?.(agentId) || battleScene?.agents?.get?.(agentId);
    if (container && typeof container.x === 'number' && typeof container.y === 'number') {
      return { x: container.x, y: container.y };
    }
    const sprite = pma?.agentSprites?.get?.(agentId);
    if (sprite && typeof sprite.x === 'number' && typeof sprite.y === 'number') {
      return { x: sprite.x, y: sprite.y };
    }
    return null;
  }

  /**
   * 面对面布局两个agent
   * 修正：攻击方在右侧，被攻击方在左侧
   */
  private positionAgentsFaceToFace(
    leftAgentKey: string,   // 被攻击方 (defender)
    rightAgentKey: string,  // 攻击方 (attacker)
    leftAgentColor: string, // 被攻击方颜色
    rightAgentColor: string // 攻击方颜色
  ): void {
    const spacing = this.config.agentSpacing;
    
    // 创建左侧agent (被攻击方，需面向右 → 素材默认朝左，需翻转)
    // 使用下划线帧键以匹配预加载资源命名
    this.leftAgent = this.scene.add.sprite(-spacing / 2, 0, `${leftAgentColor}_idle_00`);
    this.leftAgent.setScale(this.config.agentScale);
    this.leftAgent.setFlipX(true); // 面向右侧（翻转）
    this.leftAgent.play(`${leftAgentColor}-idle`);
    
    // 创建右侧agent (攻击方，需面向左 → 素材默认朝左，无需翻转)
    this.rightAgent = this.scene.add.sprite(spacing / 2, 0, `${rightAgentColor}_idle_00`);
    this.rightAgent.setScale(this.config.agentScale);
    this.rightAgent.setFlipX(false); // 面向左侧（默认）
    this.rightAgent.play(`${rightAgentColor}-idle`);
    
    // 创建HP条
    this.createHPBars(spacing);
    
    // 添加到战斗窗口
    this.combatWindow!.add([
      this.leftAgent, 
      this.rightAgent,
      this.leftHPBarBg!,
      this.leftHPBar!,
      this.leftHPText!,
      this.rightHPBarBg!,
      this.rightHPBar!,
      this.rightHPText!
    ]);
    
    console.log(`👥 面对面布局完成: 左侧=${leftAgentColor}(被攻击方), 右侧=${rightAgentColor}(攻击方), 间距=${spacing}px`);
  }

  /**
   * 创建HP条系统
   */
  private createHPBars(spacing: number): void {
    const barWidth = 60;
    const barHeight = 6;
    const barYOffset = -40; // HP条在agent上方40px

    // HP血条和文字精确位置调整
    const leftHPBarX = -50;    // 左侧HP血条X位置
    const rightHPBarX = 50;    // 右侧HP血条X位置
    const leftHPTextX = -50;   // 左侧HP文字X位置
    const rightHPTextX = 50;   // 右侧HP文字X位置

    // 左侧agent HP条（初始化为0宽，不显示占位数值）
    this.leftHPBarBg = this.scene.add.rectangle(
      leftHPBarX, barYOffset, barWidth, barHeight, 0x333333
    );
    this.leftHPBar = this.scene.add.rectangle(
      leftHPBarX, barYOffset, 0, barHeight, 0x00ff00
    );
    this.leftHPText = this.scene.add.text(
      leftHPTextX, barYOffset - 15, '--/--',
      { fontSize: '12px', color: '#ffffff' }
    ).setOrigin(0.5);

    // 右侧agent HP条（初始化为0宽，不显示占位数值）
    this.rightHPBarBg = this.scene.add.rectangle(
      rightHPBarX, barYOffset, barWidth, barHeight, 0x333333
    );
    this.rightHPBar = this.scene.add.rectangle(
      rightHPBarX, barYOffset, 0, barHeight, 0x00ff00
    );
    this.rightHPText = this.scene.add.text(
      rightHPTextX, barYOffset - 15, '--/--',
      { fontSize: '12px', color: '#ffffff' }
    ).setOrigin(0.5);

    // 确保HP条位于分层精灵之上（分层精灵使用 ~1004 深度）
    const UI_DEPTH = 1010;
    this.leftHPBarBg.setDepth(UI_DEPTH);
    this.leftHPBar.setDepth(UI_DEPTH + 1);
    this.leftHPText.setDepth(UI_DEPTH + 2);
    this.rightHPBarBg.setDepth(UI_DEPTH);
    this.rightHPBar.setDepth(UI_DEPTH + 1);
    this.rightHPText.setDepth(UI_DEPTH + 2);

    console.log(`💊 创建HP条系统: 左侧(${leftHPBarX}, ${barYOffset}) 右侧(${rightHPBarX}, ${barYOffset})`);
  }

  /**
   * 设置深度层级
   */
  private setupDepthLayers(): void {
    this.overlay?.setDepth(DEPTH_OVERLAY);
    this.combatWindow?.setDepth(DEPTH_THEATER);
    this.leftAgent?.setDepth(DEPTH_THEATER + 1);
    this.rightAgent?.setDepth(DEPTH_THEATER + 1);
  }

  /**
   * 播放攻击动画 (左打右 或 右打左)
   */
  playAttackAnimation(attackerSide: 'left' | 'right', attackAnimKey: string): Promise<void> {
    const attacker = attackerSide === 'left' ? this.leftAgent : this.rightAgent;
    
    if (!attacker) {
      return Promise.reject(new Error('Attacker sprite not found'));
    }

    return new Promise((resolve) => {
      // 播放攻击动画
      attacker.play(attackAnimKey);
      
      // 监听动画完成
      attacker.once('animationcomplete', () => {
        // 回到统一 idle key（连字符）
        const color = attackerSide === 'left' ? this.leftColor : this.rightColor;
        attacker.play(`${color}-idle`);
        resolve();
      });
      
      console.log(`⚔️ 播放${attackerSide}侧攻击动画: ${attackAnimKey}`);
    });
  }

  /** 播放idle（用于外部在分层武器动画后恢复） */
  playIdle(side: 'left' | 'right'): void {
    const sprite = side === 'left' ? this.leftAgent : this.rightAgent;
    if (!sprite) return;
    const color = side === 'left' ? this.leftColor : this.rightColor;
    sprite.play(`${color}-idle`);
  }

  /** 设置某侧Agent可见性 */
  setAgentVisible(side: 'left' | 'right', visible: boolean): void {
    const sprite = side === 'left' ? this.leftAgent : this.rightAgent;
    sprite?.setVisible(visible);
  }

  /**
   * 在剧院内播放受击动画（优先播放自制'hurt'，否则回退抖动+闪烁）
   */
  playHurtAnimation(defenderSide: 'left' | 'right'): void {
    const target = defenderSide === 'left' ? this.leftAgent : this.rightAgent;
    if (!target) return;

    const color = defenderSide === 'left' ? this.leftColor : this.rightColor;
    const hurtKey = `${color}_hurt`;
    if (this.scene.anims.exists(hurtKey)) {
      target.play(hurtKey);
      target.once('animationcomplete', () => target.play(`${color}-idle`));
    } else {
      // 回退：抖动 + 闪烁
      this.scene.tweens.add({
        targets: target,
        x: target.x + (defenderSide === 'left' ? -6 : 6),
        yoyo: true,
        repeat: 2,
        duration: 60
      });
      target.setTint(0xffaaaa);
      this.scene.time.delayedCall(180, () => target.clearTint());
    }
  }

  /**
   * 更新HP条显示
   */
  updateHPBar(agentSide: 'left' | 'right', currentHP: number, maxHP: number): void {
    const hpBarBg = agentSide === 'left' ? this.leftHPBarBg : this.rightHPBarBg;
    const hpBar = agentSide === 'left' ? this.leftHPBar : this.rightHPBar;
    const hpText = agentSide === 'left' ? this.leftHPText : this.rightHPText;
    
    if (!hpBarBg || !hpBar || !hpText) {
      console.warn(`HP条组件未找到: ${agentSide}`);
      return;
    }
    
    // 计算HP百分比（防止除0）
    const hpPercent = maxHP > 0 ? Math.max(0, currentHP / maxHP) : 0;
    const barWidth = 60;
    
    // 更新HP条宽度和颜色
    hpBar.setDisplaySize(barWidth * hpPercent, 6);
    
    // 根据HP百分比改变颜色
    if (hpPercent > 0.6) {
      hpBar.setFillStyle(0x00ff00); // 绿色
    } else if (hpPercent > 0.3) {
      hpBar.setFillStyle(0xffff00); // 黄色  
    } else {
      hpBar.setFillStyle(0xff0000); // 红色
    }
    
    // 更新内部状态 + 文本
    if (agentSide === 'left') {
      this.hpState.left.current = currentHP;
      this.hpState.left.max = maxHP;
    } else {
      this.hpState.right.current = currentHP;
      this.hpState.right.max = maxHP;
    }
    hpText.setText(`${Math.max(0, Math.floor(currentHP))}/${maxHP}`);
    
    console.log(`💊 更新${agentSide}侧HP条: ${currentHP}/${maxHP} (${Math.round(hpPercent * 100)}%)`);
  }

  /**
   * 设置HP并刷新
   */
  setHP(agentSide: 'left' | 'right', currentHP: number, maxHP: number): void {
    this.updateHPBar(agentSide, currentHP, maxHP);
  }

  /** 将HP条和文字拉到最前（防止被分层武器精灵遮挡） */
  bringHPBarsToFront(): void {
    const container = this.combatWindow as any;
    try {
      const items = [this.leftHPBarBg, this.leftHPBar, this.leftHPText, this.rightHPBarBg, this.rightHPBar, this.rightHPText].filter(Boolean) as Phaser.GameObjects.GameObject[];
      if (container && typeof container.bringToTop === 'function') {
        items.forEach(it => container.bringToTop(it));
      }
    } catch {}
  }

  /**
   * 造成伤害（会自动扣减并刷新）
   */
  applyDamage(agentSide: 'left' | 'right', damage: number): void {
    const state = agentSide === 'left' ? this.hpState.left : this.hpState.right;
    // 若最大生命值未知（0），跳过扣血更新，等待事件提供真实 current/max
    if (!state.max || state.max <= 0) {
      console.warn(`⚠️ 剧院HP未初始化，跳过扣血: side=${agentSide}, damage=${damage}`);
      return;
    }
    const newHP = Math.max(0, (state.current || 0) - Math.max(0, damage));
    this.updateHPBar(agentSide, newHP, state.max);
  }

  /** 判断某侧HP是否已初始化（max>0） */
  public isHpInitialized(side: 'left' | 'right'): boolean {
    const s = side === 'left' ? this.hpState.left : this.hpState.right;
    return !!(s.max && s.max > 0);
  }

  /**
   * 播放死亡动画
   */
  playDeathAnimation(agentSide: 'left' | 'right', deathAnimKey: string): Promise<void> {
    const agent = agentSide === 'left' ? this.leftAgent : this.rightAgent;
    
    if (!agent) {
      return Promise.reject(new Error('Agent sprite not found'));
    }

    return new Promise((resolve) => {
      // 确保可见
      agent.setVisible(true);

      // 动画存在性检查，缺失则回退
      const exists = this.scene.anims.exists(deathAnimKey);
      console.log(`💀 尝试播放死亡动画: key=${deathAnimKey}, exists=${exists}`);
      if (!exists) {
        const keys = Object.keys((this.scene.anims as any).anims?.entries || {});
        console.warn(`Death animation key not found: ${deathAnimKey}, available=`, keys);
        console.warn(`Fallback to fade-out`);
        this.scene.tweens.add({
          targets: agent,
          alpha: 0,
          duration: 800,
          onComplete: () => resolve()
        });
        return;
      }

      // 播放死亡动画
      agent.play(deathAnimKey);
      
      // 监听动画完成
      agent.once('animationcomplete', () => {
        // 死亡后变暗
        agent.setTint(0x888888);
        resolve();
      });
      
      console.log(`💀 播放${agentSide}侧死亡动画: ${deathAnimKey}`);
    });
  }

  /**
   * 关闭剧院模式
   */
  closeTheaterMode(): void {
    if (!this.isActive) {
      console.warn('⚠️ CombatTheaterWindow: 剧院模式未激活');
      return;
    }

    console.log('🎭 CombatTheaterWindow: 关闭剧院模式');

    this.stopFollowing();
    if (this.resizeListener) {
      this.scene.scale.off('resize', this.resizeListener, this);
      this.resizeListener = undefined;
    }

    // 销毁所有组件
    if (this.theaterContainer) {
      this.theaterContainer.destroy();
      this.theaterContainer = undefined;
    }

    // 重置引用
    this.overlay = undefined;
    this.combatWindow = undefined;
    this.windowBackground = undefined;
    this.leftAgent = undefined;
    this.rightAgent = undefined;
    
    // 重置HP条引用
    this.leftHPBarBg = undefined;
    this.leftHPBar = undefined;
    this.leftHPText = undefined;
    this.rightHPBarBg = undefined;
    this.rightHPBar = undefined;
    this.rightHPText = undefined;

    this.isActive = false;
    console.log('✅ CombatTheaterWindow: 剧院模式关闭完成');
  }

  /**
   * 检查是否处于激活状态
   */
  isTheaterActive(): boolean {
    return this.isActive;
  }

  /**
   * 获取某侧Agent在屏幕坐标中的位置（用于外部模块对齐绘制）
   */
  getAgentScreenPosition(side: 'left' | 'right'): { x: number; y: number } | null {
    if (!this.combatWindow) return null;
    const spacing = this.config.agentSpacing;
    const baseX = this.combatWindow.x;
    const baseY = this.combatWindow.y;
    return side === 'left'
      ? { x: baseX - spacing / 2, y: baseY }
      : { x: baseX + spacing / 2, y: baseY };
  }

  /**
   * 获取战斗窗口位置信息 (用于其他UI元素定位)
   */
  getWindowBounds(): { x: number; y: number; width: number; height: number } | null {
    if (!this.isActive || !this.combatWindow) {
      return null;
    }

    const cam = this.scene.cameras.main;
    const windowWidth = cam.width * this.config.windowWidthPercent;
    const windowHeight = cam.height * this.config.windowHeightPercent;

    return {
      x: this.combatWindow.x - windowWidth / 2,
      y: this.combatWindow.y - windowHeight / 2,
      width: windowWidth,
      height: windowHeight
    };
  }

  /**
   * 获取剧院战斗窗口的容器（用于外部将临时分层精灵挂载进来统一管理）
   */
  public getContainer(): Phaser.GameObjects.Container | null {
    return this.combatWindow ?? null;
  }

  /** 获取基础Agent精灵的缩放（用于分层动画保持一致的显示尺寸） */
  public getBaseSpriteScale(side?: 'left' | 'right'): number {
    if (side === 'left' && this.leftAgent) return this.leftAgent.scaleX;
    if (side === 'right' && this.rightAgent) return this.rightAgent.scaleX;
    return this.config.agentScale;
  }
}

export default CombatTheaterWindow;
